import {
  LicenseDto,
  LicenseStatus,
  SimpleMessageService,
  Tenant,
  WebAppCommonModule,
} from "@allbesmart/web-app-common";
import { DatePipe } from "@angular/common";
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Subscription } from "rxjs";
import { LICENSE_STATUS_CONFIG } from "src/app/configs/license-status-config";
import { ActionCommand } from "src/app/enums/action-command";
import { ActionType } from "src/app/enums/action-type";
import { LicenseActionDto } from "src/app/models/action/action-dto";
import { UpdateTenantDto } from "src/app/models/tenant/update-tenant-dto";
import { ActionsService } from "src/app/services/actions.service";
import { TenantManagementService } from "src/app/services/tenant-management.service";
import { DaysRemainingPipe } from "../../pipes/days-remaining.pipe";

@Component({
  selector: "oaibox-license-client-row",
  templateUrl: "./license-client-row.component.html",
  styleUrl: "./license-client-row.component.css",
  imports: [
    WebAppCommonModule,
    ReactiveFormsModule,
    DatePipe,
    DaysRemainingPipe,
  ],
})
export class LicenseClientRowComponent implements OnInit, OnDestroy {
  @Input({ required: true }) public tenant!: Tenant;

  protected readonly AVAILABLE_LICENSE_STATUS = Object.values(LicenseStatus);
  protected readonly LICENSE_STATUS_CONFIG = LICENSE_STATUS_CONFIG;
  protected readonly LicenseStatus = LicenseStatus;

  protected updateLicenseForm: UpdateLicenseFormGroup;
  private updateLicenseSubscription?: Subscription;

  constructor(
    private actionsService: ActionsService,
    private tenantManagementService: TenantManagementService,
    private simpleMessageService: SimpleMessageService
  ) {
    const today = new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"

    this.updateLicenseForm = new FormGroup({
      status: new FormControl<LicenseStatus | null>(null),
      startDate: new FormControl<string | null>(today),
      endDate: new FormControl<string | null>(today),
    });
  }

  ngOnInit(): void {
    if (this.tenant.license) {
      const status = this.tenant.license.status;
      this.updateLicenseForm.controls.status.patchValue(status ?? null);
      if (status === LicenseStatus.PERPETUAL) {
        this.updateLicenseForm.controls.endDate.disable();
      }

      const startTimestamp = this.tenant.license.startTimestamp;
      if (startTimestamp) {
        const startDate = new Date(startTimestamp).toISOString().slice(0, 10);
        this.updateLicenseForm.controls.startDate.patchValue(startDate);
      }

      const endTimestamp = this.tenant.license.endTimestamp;
      if (endTimestamp) {
        const endDate = new Date(endTimestamp).toISOString().slice(0, 10);
        this.updateLicenseForm.controls.endDate.patchValue(endDate);
      }
    }
  }

  protected onSelectLicenseStatus(status: LicenseStatus): void {
    const statusFormControl = this.updateLicenseForm.controls.status;
    statusFormControl.patchValue(status);
    statusFormControl.markAsDirty();

    const endDateFormControl = this.updateLicenseForm.controls.endDate;
    if (status === LicenseStatus.PERPETUAL) endDateFormControl.disable();
    else endDateFormControl.enable();
  }

  protected saveConfiguration(): void {
    if (this.updateLicenseForm.invalid) return;

    const licenseDto: LicenseDto = {
      status: this.updateLicenseForm.value.status ?? undefined,
    };

    const startDate = this.updateLicenseForm.value.startDate;
    if (startDate) {
      const startTimestamp = new Date(startDate).getTime();
      licenseDto.startTimestamp = startTimestamp;
    }

    const endDate = this.updateLicenseForm.value.endDate;
    if (endDate) {
      const endTimestamp = new Date(endDate).getTime();
      licenseDto.endTimestamp = endTimestamp;
    }

    const actionDto: LicenseActionDto = {
      type: ActionType.LICENSE,
      command: ActionCommand.UPDATE_LICENSE,
      license: licenseDto,
    };

    const tenantId = this.tenant.id;
    this.tenant.registeredMachines.forEach((machine) => {
      this.actionsService.publishAction(tenantId, machine.id, actionDto);
    });

    const updateTenantDto: UpdateTenantDto = {
      ...this.tenant,
      license: {
        status: licenseDto.status,
        startTimestamp: licenseDto.startTimestamp,
        endTimestamp: licenseDto.endTimestamp,
      },
      offline: this.tenant.offline
        ? { enable: this.tenant.offline.enable ?? false }
        : undefined,
    };

    this.updateLicenseSubscription?.unsubscribe();
    this.updateLicenseSubscription = this.tenantManagementService
      .updateTenant(this.tenant.id, updateTenantDto)
      .subscribe({
        next: (updatedTenant) => {
          this.tenant = updatedTenant;
          this.tenantManagementService.refreshTenants();
          this.simpleMessageService.showSimpleMessage({
            message: "License updated successfully!",
            icon: "fa-solid fa-check-double",
            color: "success",
          });
        },
        error: () => {
          this.simpleMessageService.showSimpleMessage({
            message: "Failed to update license!",
            icon: "fa-solid fa-triangle-exclamation",
            color: "danger",
          });
        },
      });
  }

  ngOnDestroy(): void {
    this.updateLicenseSubscription?.unsubscribe();
  }
}

type UpdateLicenseFormGroup = FormGroup<{
  status: FormControl<LicenseStatus | null>;
  startDate: FormControl<string | null>;
  endDate: FormControl<string | null>;
}>;
